import useAnalytics from '../../hooks/useAnalytics';
import useAccountInfo from '../../hooks/useAccountInfo';
import { useCallback, useEffect, useRef } from 'react';

const INITIAL_POLLING_INTERVAL = 5 * 1000; // 5 seconds in milliseconds
const MAX_POLLING_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const BACKOFF_FACTOR = 1.5; // Each interval will be 1.5x longer than the previous
const BACKOFF_START_TIME = 2 * 60 * 60 * 1000; // Start backing off after 2 hours

export function useAccountDataPolling() {
  const { data: accountInfoData, refetch: refetchAccountInfo } = useAccountInfo();
  const analytics = useAnalytics();
  const pollingIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const pollingStartTimeRef = useRef<number | undefined>(undefined);
  const currentIntervalRef = useRef<number>(INITIAL_POLLING_INTERVAL);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = undefined;
    }
  }, []);

  const onMetricsReceived = useCallback(() => {
    stopPolling();
    analytics.capture('user_redirected_to_webapp_after_metrics');
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling(); // cleanup any existing polling

    pollingStartTimeRef.current = Date.now();
    currentIntervalRef.current = INITIAL_POLLING_INTERVAL;

    const pollAccountInfo = async () => {
      try {
        refetchAccountInfo();
        if (accountInfoData?.hasMetrics) {
          onMetricsReceived();
          return;
        }

        // Check if we should stop polling completely
        if (Date.now() - pollingStartTimeRef.current! >= MAX_POLLING_TIME) {
          analytics.capture('polling_account_info_timeout');
          stopPolling();
          return;
        }

        // Check if we should increase the interval
        if (Date.now() - pollingStartTimeRef.current! >= BACKOFF_START_TIME) {
          const newInterval = Math.min(
            currentIntervalRef.current * BACKOFF_FACTOR,
            MAX_POLLING_TIME,
          );

          if (newInterval !== currentIntervalRef.current) {
            currentIntervalRef.current = newInterval;
            // Reset the interval with the new timing
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = setInterval(
              pollAccountInfo,
              currentIntervalRef.current,
            );
          }
        }
      } catch (error) {
        console.error('Failed to fetch account info during polling', { error });
      }
    };

    pollingIntervalRef.current = setInterval(pollAccountInfo, currentIntervalRef.current);
    void pollAccountInfo();
  }, [accountInfoData, refetchAccountInfo, onMetricsReceived, stopPolling]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
  };
}
